import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../assets/images/Logo.svg';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err?.data?.error || err.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 flex items-center justify-center overflow-y-auto">
      <div className="w-full max-w-[1400px] flex flex-col lg:flex-row gap-6 min-h-full py-4">
        {/* Left Side - Login Form */}
        <div className="w-full lg:w-[606px] bg-white backdrop-blur-[8.65px] rounded-xl p-4 sm:p-6 flex flex-col lg:min-h-[600px] lg:max-h-[881px] mx-auto lg:mx-0">
          {/* Logo */}
          <div className="flex justify-center mb-4 lg:mb-0 flex-shrink-0">
            <img src={Logo} alt="ProFound" className="h-8 sm:h-10 w-auto max-w-[157px]" />
          </div>

          {/* Form Content - Scrollable on small screens */}
          <div className="flex-1 flex flex-col min-h-0 px-4 sm:px-9 py-4 lg:py-0">
            <div className="flex flex-col gap-4 sm:gap-6 items-center flex-1 min-h-0">
              {/* Title and Description */}
              <div className="text-center space-y-2 w-full flex-shrink-0">
                <h1 className="text-2xl sm:text-[32px] font-medium text-[#212529] tracking-[-0.64px] leading-[1.4]">
                  Login to access your<br className="hidden sm:block" /> COA management portal
                </h1>
                <p className="text-xs sm:text-sm text-[#73827c] leading-[1.7]">
                  Request new Licences; review your pending; existing or expired Licences; access COA support documents or services; communicate with the COA management team.
                </p>
              </div>

              {/* Form - Scrollable container */}
              <form onSubmit={handleSubmit} className="w-full space-y-4 sm:space-y-5 flex-1 flex flex-col min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto space-y-4 sm:space-y-5 pr-1">
                  {sessionExpired && !error && (
                    <div className="w-full px-3 py-2 rounded bg-amber-50 text-amber-800 text-sm border border-amber-200 flex-shrink-0">
                      Your session has expired. Please log in again.
                    </div>
                  )}
                  {error && (
                    <div className="w-full px-3 py-2 rounded bg-red-50 text-red-700 text-sm border border-red-200 flex-shrink-0">{error}</div>
                  )}
                  
                  {/* Email Field */}
                  <div className="space-y-1 flex-shrink-0">
                    <label className="text-sm text-[#485b54] leading-[1.7]">
                      Email*
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="eg. example123@email.com"
                      className="w-full px-3 py-2 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Password Field */}
                  <div className="space-y-1 flex-shrink-0">
                    <label className="text-sm text-[#485b54] leading-[1.7]">
                      Password*
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Password"
                        className="w-full px-3 py-2 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          {showPassword ? (
                            <>
                              <path d="M12.9833 10C12.9833 11.6583 11.6583 12.9833 10 12.9833C8.34166 12.9833 7.01666 11.6583 7.01666 10C7.01666 8.34166 8.34166 7.01666 10 7.01666C11.6583 7.01666 12.9833 8.34166 12.9833 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9.99999 16.8917C12.9417 16.8917 15.6833 15.1583 17.5917 12.1583C18.3417 10.9833 18.3417 9.00834 17.5917 7.83334C15.6833 4.83334 12.9417 3.1 9.99999 3.1C7.05832 3.1 4.31666 4.83334 2.40832 7.83334C1.65832 9.00834 1.65832 10.9833 2.40832 12.1583C4.31666 15.1583 7.05832 16.8917 9.99999 16.8917Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </>
                          ) : (
                            <>
                              <path d="M12.1083 7.89166L7.89166 12.1083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12.9833 10C12.9833 11.6583 11.6583 12.9833 10 12.9833C8.34166 12.9833 7.01666 11.6583 7.01666 10C7.01666 8.34166 8.34166 7.01666 10 7.01666C11.6583 7.01666 12.9833 8.34166 12.9833 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9.99999 16.8917C12.9417 16.8917 15.6833 15.1583 17.5917 12.1583C18.3417 10.9833 18.3417 9.00834 17.5917 7.83334C15.6833 4.83334 12.9417 3.1 9.99999 3.1C7.05832 3.1 4.31666 4.83334 2.40832 7.83334C1.65832 9.00834 1.65832 10.9833 2.40832 12.1583C4.31666 15.1583 7.05832 16.8917 9.99999 16.8917Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </>
                          )}
                        </svg>
                      </button>
                    </div>

                    {/* Remember Me & Forgot Password */}
                    <div className="flex items-center justify-between py-2 flex-wrap gap-2">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative flex items-center justify-center w-5 h-5 rounded border border-[#40c191] group-hover:border-[#40c191] transition-colors flex-shrink-0">
                          <input
                            type="checkbox"
                            name="rememberMe"
                            checked={formData.rememberMe}
                            onChange={handleChange}
                            className="absolute opacity-0 w-full h-full cursor-pointer border-[#40c191] rounded"
                          />
                          {formData.rememberMe && (
                            <svg className="w-3.5 h-3.5 text-[#40c191]" viewBox="0 0 14 14" fill="none">
                              <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span className="text-[14px] text-[#485b54] select-none">Remember me</span>
                      </label>
                      <Link to="/forgot-password" className="text-xs sm:text-sm text-[#485b54] underline hover:text-emerald-400">
                        Forgot Password
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Login Button - Always visible at bottom */}
                <div className="flex-shrink-0 space-y-3 sm:space-y-4 pt-2">
                  <button
                    type="submit"
                    className="w-full h-10 sm:h-11 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity shadow-sm"
                  >
                    Login
                  </button>

                  {/* Register Link */}
                  <div className="flex items-center justify-center gap-1 flex-wrap">
                    <span className="text-xs sm:text-sm text-[#adb5bd] leading-[1.7]">Don't have an account?</span>
                    <Link to="/register" className="text-xs sm:text-sm text-[#485b54] underline hover:text-emerald-400">
                      Register
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Footer - Only show on larger screens or if there's space */}
          <div className="text-center mt-4 lg:mt-0 flex-shrink-0 hidden sm:block">
            <p className="text-xs sm:text-sm text-[#485b54] leading-[1.7]">All Rights Reserved © 2025</p>
          </div>
        </div>

        {/* Right Side - Gradient Background - Hidden on mobile, shown on larger screens */}
        <div className="hidden lg:flex flex-1  rounded-xl relative overflow-hidden bg-gradient-to-br from-cyan-300 via-emerald-200 to-emerald-100">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#7DD3DD] via-[#A8E6CF] to-[#C8F7DC]"></div>
            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_50%,rgba(125,211,221,0.4)_0%,transparent_50%)]"></div>
            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_70%_50%,rgba(168,230,207,0.4)_0%,transparent_50%)]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
